import { useCallback, useEffect, useRef, type MouseEvent } from 'react';
import { renderInline, renderMarkdown } from '../../lib/markdown';
import { useRefNav } from '../../lib/refNav';

export interface MarkdownViewProps {
  /** Markdown source. */
  children?: string;
  /** Render as a single inline line (no block wrapping). */
  inline?: boolean;
  className?: string;
}

/**
 * Render sanitized markdown. Used for descriptions, custom pages and notes preview.
 *
 * When rendered inside a running binder (RefNav context present), `[[kind:id]]` wiki-links
 * become clickable: resolvable links get an href + navigate within the SPA on click;
 * unresolved links are marked and inert.
 */
export function MarkdownView({ children, inline = false, className }: MarkdownViewProps) {
  const refNav = useRefNav();
  const containerRef = useRef<HTMLDivElement>(null);
  const html = inline ? renderInline(children) : renderMarkdown(children);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const links = root.querySelectorAll<HTMLAnchorElement>('a.wikilink[data-ref]');
    links.forEach((a) => {
      const ref = a.getAttribute('data-ref') ?? '';
      const href = refNav?.hrefFor(ref) ?? null;
      if (href) {
        a.setAttribute('href', href);
        a.classList.remove('wikilink-missing');
        const target = refNav?.resolve(ref);
        if (target) a.setAttribute('title', target.label);
      } else {
        a.removeAttribute('href');
        a.classList.add('wikilink-missing');
        a.setAttribute('title', ref);
      }
    });
  }, [html, refNav]);

  const onClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (!refNav) return;
      const anchor = (e.target as HTMLElement).closest('a.wikilink[data-ref]');
      if (!anchor) return;
      const ref = anchor.getAttribute('data-ref');
      if (!ref) return;
      // Let modified clicks (new tab, etc.) behave natively.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      refNav.navigateRef(ref);
    },
    [refNav],
  );

  return (
    <div
      ref={containerRef}
      className={['markdown', className].filter(Boolean).join(' ')}
      onClick={refNav ? onClick : undefined}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
